import type * as THREE from 'three'
import type { Ref } from 'vue'
import { ImportedModelObjectMetadataService } from '@puppycad/cad-core'
import type {
  EntitySelectedEventPayload,
  SelectionInteractionMeta,
  Viewer,
} from '@puppycad/cad-core'

export interface ImportedModelSelectionBindingLookup {
  getTreeKeyByObjectId: (objectId: string) => string | undefined
  getTreeKeyByBodyBindingId?: (bodyBindingId: string, assemblyNodeId?: string | null) => string | undefined
  findBindingByKey: (key: string) => unknown
}

export interface UseEditorImportedModelSelectionSyncOptions {
  getViewer: () => Pick<Viewer, 'buildImportedSelectionSummary'> | null
  glbFeatureTree: ImportedModelSelectionBindingLookup
  selectedKeys: Ref<string[]>
  selectedObject: Ref<unknown>
  canSync?: () => boolean
}

/**
 * 导入模型视口选中同步器。
 *
 * 设计说明：
 * 1. 该同步器只负责 `Viewer -> 设计树/UI` 的 imported-model 联动，不参与草图或特征工作流；
 * 2. 设计树 key 的映射仍留在 web-client，因为它属于 UI 投影；选择摘要则来自 cad-core；
 * 3. 这样可以把 imported-model 回流链从 `useEditorSketchWorkflow` 中剥离，避免继续做大。
 */
export function useEditorImportedModelSelectionSync(options: UseEditorImportedModelSelectionSyncOptions) {
  const canSync = options.canSync ?? (() => true)

  /**
   * 处理实体选中事件。
   * @param payload 选中事件载荷。
   * @returns 是否已由导入模型同步器处理。
   */
  function handleEntitySelected(payload: EntitySelectedEventPayload): boolean {
    if (!canSync() || !payload.objectId || !payload.object) {
      return false
    }

    const viewer = options.getViewer()
    const summary = viewer?.buildImportedSelectionSummary?.(payload.objectId, {
      interactionMeta: payload.interactionMeta,
    })
    if (!summary) {
      return false
    }

    const treeKey = resolveTreeKeyBySelectionSummary(summary)
      ?? resolveTreeKey(summary.selectionObject ?? summary.sourceObject ?? payload.object)
    if (!treeKey) {
      return false
    }

    options.selectedKeys.value = [treeKey]
    options.selectedObject.value = summary.selectedObject
    return true
  }

  /**
   * 从 cad-core 选择摘要中优先解析 imported Body 实体节点。
   *
   * 设计说明：
   * `.puppy` 特征实体导入后，树上的 Body 是虚拟语义节点，视口里真正命中的是
   * FeatureFace / FeatureLine / FeaturePoint 或父 selection proxy。这里必须先按
   * bodyBindingId 回流，不能只按 Object3D uuid 回到根节点。
   *
   * @param summary cad-core 导入模型选择摘要。
   * @returns 设计树 key；无法定位时返回 null。
   */
  function resolveTreeKeyBySelectionSummary(summary: {
    bodyBindingId?: string
    selectedObject?: { metadata?: Record<string, unknown> }
  }): string | null {
    const metadata = summary.selectedObject?.metadata
    if (!options.glbFeatureTree.getTreeKeyByBodyBindingId) {
      return null
    }

    const summaryBodyBindingId = typeof summary.bodyBindingId === 'string'
      ? summary.bodyBindingId.trim()
      : ''
    const metadataBodyBindingId = typeof metadata?.bodyBindingId === 'string'
      ? metadata.bodyBindingId.trim()
      : ''
    const bodyBindingId = summaryBodyBindingId || metadataBodyBindingId
    if (bodyBindingId.length === 0) {
      return null
    }

    const assemblyNodeId = typeof metadata.assemblyNodeId === 'string'
      ? metadata.assemblyNodeId
      : null
    return options.glbFeatureTree.getTreeKeyByBodyBindingId(bodyBindingId, assemblyNodeId) ?? null
  }

  /**
   * 处理选择清空事件。
   * @returns 是否清除了导入模型相关 UI 状态。
   */
  function handleSelectionCleared(): boolean {
    if (!isImportedSelectionStateActive()) {
      return false
    }
    options.selectedKeys.value = []
    options.selectedObject.value = null
    return true
  }

  /**
   * 判断当前 UI 状态是否处于导入模型选中态。
   * @returns 是否为导入模型选中态。
   */
  function isImportedSelectionStateActive(): boolean {
    const selectedKey = options.selectedKeys.value[0]
    if (selectedKey && options.glbFeatureTree.findBindingByKey(selectedKey)) {
      return true
    }

    const currentSelectedObject = options.selectedObject.value as {
      metadata?: Record<string, unknown>
    } | null
    return currentSelectedObject?.metadata?.source === 'imported-model'
  }

  /**
   * 解析导入模型对象对应的设计树 key。
   * @param object 候选对象。
   * @returns 设计树 key；未命中时返回 null。
   */
  function resolveTreeKey(object: THREE.Object3D | null): string | null {
    let current: THREE.Object3D | null = object
    while (current) {
      const directKey = options.glbFeatureTree.getTreeKeyByObjectId(current.uuid)
      if (directKey) {
        return directKey
      }

      const proxyObjectId = ImportedModelObjectMetadataService.resolveSelectionProxyObjectId(current)?.trim() ?? ''
      if (proxyObjectId.length > 0) {
        const proxyKey = options.glbFeatureTree.getTreeKeyByObjectId(proxyObjectId)
        if (proxyKey) {
          return proxyKey
        }
      }
      current = current.parent
    }
    return null
  }

  return {
    handleEntitySelected,
    handleSelectionCleared,
  }
}
